import ErrorBoundary from "../../SimpleComponents/Errors/ErrorBoundary";

import PnlSummaryLoaderSkeleton from "./PnlSummaryLoaderSkeleton";
import { PnlSummaryChartContent } from "./PnlSummaryChartContent/PnlSummaryChartContent";
import { useIsStillLoadingDataToStore } from "../../../pages/DashboardPages/LoadingIndicatorDashboardPages";
import {
  useDelayFirstLoad,
  useFocusRange,
} from "utils/customHooks/genericHooks";
import { usePnlSummaryChartSections } from "utils/customHooks/pnlChartHooks";
import { isEqual } from "lodash";
import type { TimeRangeFinite } from "types/types";
import { useObserveComponentLoadTime } from "App/FlightPerformanceObserver";
import { useMediaQuery } from "@mui/material";

type PnlSummaryChartProps = {
  timeRange?: TimeRangeFinite;
  isSubordinateProcessesOnly?: boolean;
};

const PnlSummaryChart = ({
  timeRange,
  isSubordinateProcessesOnly,
}: PnlSummaryChartProps) => {
  const isStillLoadingDataToStore = useIsStillLoadingDataToStore(true);
  const isFirstLoadComplete = useDelayFirstLoad({ delayMs: 5 * 1000 });
  const focusRange = useFocusRange();
  const isPopup = Boolean(timeRange && !isEqual(timeRange, focusRange));
  const pnlBreakdownChartCategorySectionsArr = usePnlSummaryChartSections({
    isPopup,
    isSubordinateProcessesOnly,
  });

  const timeRangeToUse = timeRange || focusRange;

  const isPrint = useMediaQuery("print");
  const ready =
    isPrint ||
    (!isStillLoadingDataToStore &&
      isFirstLoadComplete &&
      pnlBreakdownChartCategorySectionsArr.length > 0);

  useObserveComponentLoadTime({
    name: "PnL Chart",
    loading: !ready,
  });

  if (!ready) {
    // wait until we have a process
    return (
      <ErrorBoundary id={"PnlSummaryLoaderSkeleton"}>
        <PnlSummaryLoaderSkeleton {...{ isSubordinateProcessesOnly }} />
      </ErrorBoundary>
    );
  }

  const pnlSummaryChartContentProps = {
    pnlBreakdownChartCategorySectionsArr,
    isSubordinateProcessesOnly,
    timeRange: timeRangeToUse as TimeRangeFinite,
  };

  return (
    focusRange && (
      <ErrorBoundary id={"PnlSummaryChartContent"}>
        <div>
          <PnlSummaryChartContent {...pnlSummaryChartContentProps} />
        </div>
      </ErrorBoundary>
    )
  );
};

export default PnlSummaryChart;
